import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Sparkles,
  RefreshCw,
  Plus,
  Loader2,
  Target,
  Zap,
  Heart,
  Rocket,
  Calendar,
  Trophy,
  FileText,
  X,
  type LucideIcon,
} from 'lucide-react';
import { clsx } from 'clsx';
import { agentLabApi, generationApi, type BriefIdea, type BriefIdeaDrivingFactor } from '@/services/api';

// ---------------------------------------------------------------------------
// Swim lane visual mapping
// ---------------------------------------------------------------------------

interface LaneVisual {
  label: string;
  icon: LucideIcon;
  iconColor: string;
  barColor: string;
}

const LANE_VISUALS: Record<string, LaneVisual> = {
  acquisition:    { label: 'Acquisition',    icon: Target,   iconColor: 'text-teal-500',    barColor: 'from-teal-400 to-teal-500' },
  conversion:     { label: 'Conversion',     icon: Zap,      iconColor: 'text-indigo-500',  barColor: 'from-indigo-400 to-indigo-500' },
  retention:      { label: 'Retention',      icon: Heart,    iconColor: 'text-rose-500',    barColor: 'from-rose-400 to-rose-500' },
  product_launch: { label: 'Product Launch', icon: Rocket,   iconColor: 'text-amber-500',   barColor: 'from-amber-400 to-amber-500' },
  seasonal:       { label: 'Seasonal',       icon: Calendar, iconColor: 'text-sky-500',     barColor: 'from-sky-400 to-sky-500' },
  competitive:    { label: 'Competitive',    icon: Trophy,   iconColor: 'text-emerald-500', barColor: 'from-emerald-400 to-emerald-500' },
  drafts:         { label: 'Drafts',         icon: FileText, iconColor: 'text-slate-400',   barColor: 'from-slate-300 to-slate-400' },
};

const CAMPAIGN_TO_LANE: Record<string, string> = {
  awareness: 'acquisition', prospecting: 'acquisition', acquisition: 'acquisition',
  consideration: 'conversion', conversion: 'conversion',
  retention: 'retention', loyalty: 'retention',
  product_launch: 'product_launch', launch: 'product_launch',
  seasonal: 'seasonal', holiday: 'seasonal',
  competitive: 'competitive', conquest: 'competitive',
};

function getIdeaSwimLane(idea: BriefIdea): string {
  const ct =
    idea.campaign_type?.toLowerCase() ??
    idea.chain_suggestion?.params?.campaign_type?.toLowerCase() ??
    '';
  if (ct && CAMPAIGN_TO_LANE[ct]) return CAMPAIGN_TO_LANE[ct];
  const premise = idea.premise?.toLowerCase() ?? '';
  if (premise.includes('retain') || premise.includes('loyal') || premise.includes('win-back')) return 'retention';
  if (premise.includes('launch') || premise.includes('new product') || premise.includes('debut')) return 'product_launch';
  if (premise.includes('compete') || premise.includes('vs ') || premise.includes('conquest')) return 'competitive';
  if (premise.includes('season') || premise.includes('holiday')) return 'seasonal';
  return 'acquisition';
}

// ---------------------------------------------------------------------------
// Edge type → color mapping for driving factor tags
// ---------------------------------------------------------------------------

const EDGE_COLOR: Record<string, string> = {
  DRIVES:           'bg-teal-50   text-teal-700   border-teal-200   hover:bg-teal-100',
  RESOLVES_CONCERN: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
  INHIBITS:         'bg-rose-50   text-rose-700   border-rose-200   hover:bg-rose-100',
  SUPPORTS:         'bg-amber-50  text-amber-700  border-amber-200  hover:bg-amber-100',
  // Synthetic (fallback) tags use a muted slate palette to distinguish from real PDO data
  SYNTHETIC:        'bg-slate-50  text-slate-500  border-slate-200  hover:bg-slate-100',
};

const EDGE_DEFAULT = 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100';

function edgeColorClass(edgeType: string): string {
  return EDGE_COLOR[edgeType?.toUpperCase()] ?? EDGE_DEFAULT;
}

// ---------------------------------------------------------------------------
// Fallback factor generation — derived from available data when PDO is absent
// ---------------------------------------------------------------------------

/**
 * Keyword clusters for premise-based tagging.
 * Each entry maps a display label → search terms to look for in the premise.
 * The edge_type "SYNTHETIC" triggers the muted style and steers with a
 * product_hint built from the label.
 */
const PREMISE_TAG_MAP: Array<{ concept: string; keywords: string[] }> = [
  { concept: 'Trust & Credibility',  keywords: ['trust', 'credib', 'authentic', 'honest', 'real', 'proof', 'verif'] },
  { concept: 'Discovery Moment',     keywords: ['discover', 'find ', 'explore', 'uncover', 'reveal', 'unknown'] },
  { concept: 'Urgency & Action',     keywords: ['seize', 'act now', 'moment', "don't miss", 'limited', 'now', 'fast'] },
  { concept: 'Identity & Self',      keywords: ['who you are', 'your true', 'identity', 'yourself', 'self', 'belong'] },
  { concept: 'Transformation',       keywords: ['transform', 'change', 'become', 'journey', 'before', 'after'] },
  { concept: 'Community & Belonging',keywords: ['community', 'together', 'connect', 'join', 'belong', 'shared'] },
  { concept: 'Aspiration',           keywords: ['aspir', 'dream', 'potential', 'vision', 'future', 'ideal'] },
  { concept: 'Problem → Solution',   keywords: ['problem', 'struggle', 'solut', 'answer', 'fix', 'resolve'] },
  { concept: 'Value & Reward',       keywords: ['value', 'reward', 'worth', 'deserve', 'earn', 'exclusive'] },
  { concept: 'Adventure & Freedom',  keywords: ['adventure', 'freedom', 'wild', 'escape', 'bold', 'brave'] },
];

/** Map resonance dimension strength → display tag */
function dimensionTag(breakdown: BriefIdea['resonance_breakdown']): { concept: string; edge_type: string } | null {
  if (!breakdown) return null;
  const { scenario_fit = 0, intent_demand = 0, differentiation = 70 } = breakdown;
  // Only surface a dimension tag when a score is meaningfully above baseline
  if (intent_demand > 25) return { concept: 'Search Intent Signal',   edge_type: 'SYNTHETIC' };
  if (scenario_fit > 60)  return { concept: 'Scenario Grounded',     edge_type: 'SYNTHETIC' };
  if (differentiation > 90) return { concept: 'High Novelty Angle', edge_type: 'SYNTHETIC' };
  return null;
}

/**
 * Generate up to 3 synthetic BriefIdeaDrivingFactor-shaped objects from
 * whatever signal is available (premise keywords + dimension scores).
 * All have edge_type = "SYNTHETIC" so the tile renders them with muted styling.
 */
function generateFallbackFactors(idea: BriefIdea): BriefIdeaDrivingFactor[] {
  const factors: BriefIdeaDrivingFactor[] = [];
  const text = ((idea.title ?? '') + ' ' + (idea.premise ?? '')).toLowerCase();

  for (const { concept, keywords } of PREMISE_TAG_MAP) {
    if (keywords.some((kw) => text.includes(kw))) {
      factors.push({ concept, category: 'THEME', edge_type: 'SYNTHETIC' });
      if (factors.length >= 2) break;
    }
  }

  // Pad with a dimension-derived tag if under 2 entries
  if (factors.length < 2) {
    const dim = dimensionTag(idea.resonance_breakdown);
    if (dim && !factors.some((f) => f.concept === dim.concept)) {
      factors.push({ concept: dim.concept, category: 'SIGNAL', edge_type: 'SYNTHETIC' });
    }
  }

  // Lane-based tag as last resort
  if (factors.length === 0) {
    const lane = getIdeaSwimLane(idea);
    const laneLabel = LANE_VISUALS[lane]?.label ?? 'Acquisition';
    factors.push({ concept: `${laneLabel} Play`, category: 'STRATEGY', edge_type: 'SYNTHETIC' });
  }

  return factors.slice(0, 3);
}

/**
 * Returns real PDO-grounded factors when available, otherwise synthetic fallback.
 * Callers can check the `_synthetic` flag to adjust rendering.
 */
function resolveFactors(idea: BriefIdea): { factors: BriefIdeaDrivingFactor[]; synthetic: boolean } {
  const real = idea.driving_factors ?? [];
  if (real.length > 0) return { factors: real, synthetic: false };
  return { factors: generateFallbackFactors(idea), synthetic: true };
}

// ---------------------------------------------------------------------------
// Session-storage cache
// ---------------------------------------------------------------------------

const CACHE_TTL_MS = 10 * 60 * 1000;

function cacheKey(clientUid: string, personaId: string, hint: string): string {
  return `brief-ideas:${clientUid}:${personaId || '_'}:${hint || '_'}`;
}

interface CachedIdeas { ideas: BriefIdea[]; ts: number }

function readCache(key: string): BriefIdea[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedIdeas;
    if (!parsed || !Array.isArray(parsed.ideas)) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.ideas;
  } catch { return null; }
}

function writeCache(key: string, ideas: BriefIdea[]): void {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.setItem(key, JSON.stringify({ ideas, ts: Date.now() })); }
  catch { /* quota / private mode */ }
}

function clearCache(key: string): void {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(key); } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Aggregate theme chips from loaded ideas
// ---------------------------------------------------------------------------

interface ThemeChip {
  concept: string;
  edge_type: string;
  count: number;
}

function aggregateThemeChips(ideas: BriefIdea[], max = 6): ThemeChip[] {
  const map = new Map<string, ThemeChip>();
  for (const idea of ideas) {
    // Use resolved factors (real PDO or synthetic fallback) so chips always populate
    const { factors } = resolveFactors(idea);
    for (const f of factors) {
      const key = f.concept;
      const existing = map.get(key);
      if (existing) { existing.count++; }
      else { map.set(key, { concept: f.concept, edge_type: f.edge_type, count: 1 }); }
    }
  }
  return Array.from(map.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, max);
}

// ---------------------------------------------------------------------------
// Resonance mini-bar — three-segment (scenario_fit, intent_demand, differentiation)
// ---------------------------------------------------------------------------

interface MiniResonanceBarProps {
  scenarioFit: number;
  intentDemand: number;
  differentiation: number;
}

function MiniResonanceBar({ scenarioFit, intentDemand, differentiation }: MiniResonanceBarProps) {
  const dims = [
    { label: 'Fit',    value: scenarioFit,    color: 'bg-teal-400' },
    { label: 'Demand', value: intentDemand,   color: 'bg-indigo-400' },
    { label: 'New',    value: differentiation, color: 'bg-amber-400' },
  ];
  return (
    <div className="flex items-center gap-2">
      {dims.map(({ label, value, color }) => (
        <div key={label} className="flex-1">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[9px] text-slate-400 uppercase tracking-wide">{label}</span>
            <span className="text-[9px] font-mono text-slate-400">{Math.round(value)}</span>
          </div>
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className={clsx('h-full rounded-full transition-all', color)} style={{ width: `${value}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Driving factor tag
// ---------------------------------------------------------------------------

interface FactorTagProps {
  factor: BriefIdeaDrivingFactor;
  synthetic?: boolean;
  active?: boolean;
  onClick?: () => void;
}

function FactorTag({ factor, synthetic, active, onClick }: FactorTagProps) {
  const isSynth = synthetic || factor.edge_type === 'SYNTHETIC';
  return (
    <button
      type="button"
      onClick={onClick}
      title={
        isSynth
          ? `Explore briefs themed around "${factor.concept}"`
          : `Explore briefs grounded in "${factor.concept}" (PDO signal)`
      }
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition-all',
        active
          ? 'bg-teal-500 text-white border-teal-500 font-medium'
          : isSynth
          ? 'font-normal italic ' + EDGE_COLOR['SYNTHETIC']
          : 'font-medium ' + edgeColorClass(factor.edge_type),
      )}
    >
      {factor.concept}
    </button>
  );
}

// ---------------------------------------------------------------------------
// BriefIdeaTile
// ---------------------------------------------------------------------------

interface BriefIdeaTileProps {
  idea: BriefIdea;
  isCreating: boolean;
  disabled: boolean;
  activeFilter: string | null;
  onClick: () => void;
  onFilterByFactor: (concept: string) => void;
}

function BriefIdeaTile({ idea, isCreating, disabled, activeFilter, onClick, onFilterByFactor }: BriefIdeaTileProps) {
  const laneId = getIdeaSwimLane(idea);
  const lane = LANE_VISUALS[laneId] ?? LANE_VISUALS.drafts;
  const Icon = lane.icon;
  const resonance = Math.max(0, Math.min(100, Math.round(idea.predicted_resonance ?? 0)));
  const breakdown = idea.resonance_breakdown;
  const { factors, synthetic: isSynthetic } = resolveFactors(idea);
  const hookDirection = idea.hook_direction;

  return (
    <div
      className={clsx(
        'relative text-left bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-3',
        'transition-all duration-200',
        !disabled && !isCreating && 'hover:border-teal-300 hover:shadow-md',
        disabled && !isCreating && 'opacity-60',
        isCreating && 'cursor-wait',
      )}
    >
      {/* Lane badge + resonance score */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className={lane.iconColor} />
          <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
            {lane.label}
          </span>
        </div>
        <span
          className="text-[11px] font-bold tabular-nums text-slate-700 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5"
          title="Predicted resonance (PDO-signal-weighted heuristic)"
        >
          {resonance}
        </span>
      </div>

      {/* Clickable content area */}
      <button
        type="button"
        disabled={disabled || isCreating}
        onClick={onClick}
        className="text-left flex flex-col gap-2 flex-1 focus:outline-none group"
      >
        {/* Title */}
        <div className="font-semibold text-sm text-navy-900 line-clamp-2 leading-snug group-hover:text-teal-700 transition-colors">
          {idea.title || 'Untitled idea'}
        </div>

        {/* Premise */}
        <div className="text-xs text-slate-500 line-clamp-3 leading-relaxed">
          {idea.premise || 'No premise available.'}
        </div>

        {/* Hook direction */}
        {hookDirection && (
          <div className="text-[10px] text-slate-400 italic">
            Hook: {hookDirection}
          </div>
        )}
      </button>

      {/* Three-dimension resonance breakdown */}
      {breakdown && (
        <MiniResonanceBar
          scenarioFit={breakdown.scenario_fit ?? 0}
          intentDemand={breakdown.intent_demand ?? 0}
          differentiation={breakdown.differentiation ?? 0}
        />
      )}

      {/* Driving factor tags — "see more like this" */}
      {factors.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-slate-100">
          {factors.map((f) => (
            <FactorTag
              key={f.concept}
              factor={f}
              synthetic={isSynthetic}
              active={activeFilter === f.concept}
              onClick={() => onFilterByFactor(f.concept)}
            />
          ))}
          {isSynthetic && (
            <span
              title="Tags are theme-derived — PDO enrichment coming soon"
              className="self-center text-[9px] text-slate-300 select-none"
            >
              ~
            </span>
          )}
        </div>
      )}

      {/* Creating overlay */}
      {isCreating && (
        <div className="absolute inset-0 bg-white/85 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center gap-2">
          <Loader2 size={18} className="animate-spin text-teal-500" />
          <span className="text-xs font-medium text-slate-600">Generating brief...</span>
        </div>
      )}
    </div>
  );
}

function NewCustomBriefTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-4 flex flex-col items-center justify-center gap-2 min-h-[200px] hover:border-teal-400 hover:bg-teal-50/50 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center">
        <Plus size={18} className="text-slate-500" />
      </div>
      <div className="font-semibold text-sm text-navy-900">New Custom Brief</div>
      <div className="text-xs text-slate-500 text-center leading-snug">
        Start from a prompt, persona,
        <br />
        or uploaded source materials
      </div>
    </button>
  );
}

function BriefIdeaTileSkeleton() {
  return (
    <div className="animate-pulse bg-white rounded-xl border border-slate-200 p-4 min-h-[200px] flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="h-3 bg-slate-200 rounded w-1/3" />
        <div className="h-4 w-8 bg-slate-200 rounded-full" />
      </div>
      <div className="h-4 bg-slate-200 rounded w-3/4" />
      <div className="space-y-1.5 flex-1">
        <div className="h-3 bg-slate-200 rounded w-full" />
        <div className="h-3 bg-slate-200 rounded w-5/6" />
        <div className="h-3 bg-slate-200 rounded w-2/3" />
      </div>
      <div className="flex gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex-1">
            <div className="h-2 bg-slate-100 rounded w-full mb-1" />
            <div className="h-1 bg-slate-200 rounded w-full" />
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 pt-1 border-t border-slate-100">
        <div className="h-4 w-20 bg-slate-100 rounded-full" />
        <div className="h-4 w-16 bg-slate-100 rounded-full" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BriefSuggestionRail — exported
// ---------------------------------------------------------------------------

export interface BriefSuggestionRailProps {
  contextClientUid: string;
  contextPersonaId: string;
  personaLabel?: string;
  clientLabel?: string;
  onBriefCreated: (briefId: string) => void;
  onOpenModal: () => void;
}

export function BriefSuggestionRail({
  contextClientUid,
  contextPersonaId,
  personaLabel,
  clientLabel,
  onBriefCreated,
  onOpenModal,
}: BriefSuggestionRailProps) {
  const [ideas, setIdeas] = useState<BriefIdea[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creatingIdeaId, setCreatingIdeaId] = useState<string | null>(null);

  // Dimension steering — concept name that was clicked as "see more like this"
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Derived theme chips — aggregate resolved factors (real PDO or synthetic fallback)
  const themeChips = ideas.length > 0 ? aggregateThemeChips(ideas) : [];
  // True when all ideas are running in no-PDO fallback mode
  const allSynthetic = ideas.length > 0 && ideas.every((idea) => (idea.driving_factors ?? []).length === 0);

  const fetchIdeas = useCallback(
    async (opts?: { skipCache?: boolean; hint?: string }) => {
      if (!contextClientUid) {
        setIdeas([]);
        return;
      }
      const hint = opts?.hint ?? activeFilter ?? '';
      const key = cacheKey(contextClientUid, contextPersonaId, hint);

      if (!opts?.skipCache) {
        const cached = readCache(key);
        if (cached) {
          setIdeas(cached.slice(0, 4));
          setLoading(false);
          setError(null);
          return;
        }
      }

      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      setError(null);

      try {
        const body: { [key: string]: unknown; client_uid: string; persona_id?: string } = {
          client_uid: contextClientUid,
        };
        if (contextPersonaId) body.persona_id = contextPersonaId;
        if (hint) body.product_hint = `Focus on ideas grounded in "${hint}"`;

        const response = await agentLabApi.invokeBriefIdeas(body);
        if (ctrl.signal.aborted) return;
        const list = Array.isArray(response?.ideas) ? response.ideas.slice(0, 4) : [];
        setIdeas(list);
        if (list.length > 0) writeCache(key, list);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Could not load suggestions');
        setIdeas([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    },
    [contextClientUid, contextPersonaId, activeFilter],
  );

  // Initial fetch and re-fetch when context changes (reset filter too)
  useEffect(() => {
    setActiveFilter(null);
    fetchIdeas();
    return () => { abortRef.current?.abort(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextClientUid, contextPersonaId]);

  const handleRefresh = () => {
    if (!contextClientUid) return;
    clearCache(cacheKey(contextClientUid, contextPersonaId, activeFilter ?? ''));
    fetchIdeas({ skipCache: true, hint: activeFilter ?? undefined });
  };

  const handleFilterByFactor = (concept: string) => {
    const next = activeFilter === concept ? null : concept;
    setActiveFilter(next);
    clearCache(cacheKey(contextClientUid, contextPersonaId, next ?? ''));
    // Re-fetch immediately with new steering hint
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);

    const body: { [key: string]: unknown; client_uid: string; persona_id?: string } = {
      client_uid: contextClientUid,
    };
    if (contextPersonaId) body.persona_id = contextPersonaId;
    if (next) body.product_hint = `Focus on ideas grounded in "${next}"`;

    agentLabApi.invokeBriefIdeas(body)
      .then((response) => {
        if (ctrl.signal.aborted) return;
        const list = Array.isArray(response?.ideas) ? response.ideas.slice(0, 4) : [];
        setIdeas(list);
        if (list.length > 0) writeCache(cacheKey(contextClientUid, contextPersonaId, next ?? ''), list);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Could not load suggestions');
        setIdeas([]);
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
  };

  const handleTileClick = async (idea: BriefIdea) => {
    if (creatingIdeaId) return;
    setCreatingIdeaId(idea.idea_id);
    try {
      const params = idea.chain_suggestion?.params;
      const clientUid = params?.client_uid || contextClientUid;
      const personaId = params?.persona_id || contextPersonaId;
      if (!clientUid) {
        setError('Select a client before generating from a suggestion.');
        return;
      }
      let briefId: string;
      if (personaId) {
        const brief = await generationApi.createFromPersona({
          client_uid: clientUid,
          persona_id: personaId,
          brief_name: idea.title || undefined,
        });
        briefId = brief.brief_id;
      } else {
        const prompt = idea.premise || idea.title || 'Generate a brief from this suggestion.';
        const brief = await generationApi.synthesizeBrief({
          client_uid: clientUid,
          prompt,
          brief_name: idea.title || undefined,
        });
        briefId = brief.brief_id;
      }
      onBriefCreated(briefId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate brief');
    } finally {
      setCreatingIdeaId(null);
    }
  };

  // Context label for the subtitle pill
  const contextPill = contextPersonaId
    ? (personaLabel ?? contextPersonaId)
    : contextClientUid
      ? (clientLabel ?? contextClientUid)
      : null;

  // No-client empty state
  if (!contextClientUid) {
    return (
      <section className="rounded-xl border border-dashed border-slate-200 bg-white px-5 py-8 text-center">
        <Sparkles size={20} className="mx-auto text-teal-500 mb-2" />
        <p className="text-sm font-medium text-navy-900 mb-1">
          Select a client above to see personalized brief suggestions
        </p>
        <p className="text-xs text-slate-500 max-w-md mx-auto">
          Suggestions are grounded in the client's persona data and creative history.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4">

      {/* ── Section header ───────────────────────────────────────────── */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <Sparkles size={14} className="text-teal-500 shrink-0" />
        <h2 className="text-sm font-bold text-navy-900">Suggested Creative Ideas</h2>

        {/* Active dimension filter badge */}
        {activeFilter && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-0.5">
            {activeFilter}
            <button
              type="button"
              onClick={() => handleFilterByFactor(activeFilter)}
              className="hover:text-teal-900 transition-colors"
              title="Clear filter"
            >
              <X size={10} />
            </button>
          </span>
        )}

        {/* Refresh */}
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-teal-600 disabled:opacity-50 transition-colors"
          title="Regenerate suggestions"
        >
          <RefreshCw size={12} className={clsx('transition-transform', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* ── Theme chips ──────────────────────────────────────────────── */}
      {!loading && themeChips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-slate-400 uppercase tracking-wide font-medium shrink-0">
            Explore themes
          </span>
          {themeChips.map((chip) => {
            const isSynth = chip.edge_type === 'SYNTHETIC';
            return (
              <button
                key={chip.concept}
                type="button"
                onClick={() => handleFilterByFactor(chip.concept)}
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] transition-all',
                  activeFilter === chip.concept
                    ? 'bg-teal-500 text-white border-teal-500 font-medium'
                    : isSynth
                    ? 'font-normal italic ' + EDGE_COLOR['SYNTHETIC']
                    : 'font-medium ' + edgeColorClass(chip.edge_type),
                )}
              >
                {chip.concept}
                {chip.count > 1 && (
                  <span className={clsx(
                    'ml-0.5 opacity-60',
                    activeFilter === chip.concept ? 'text-white' : '',
                  )}>
                    {chip.count}
                  </span>
                )}
              </button>
            );
          })}
          {allSynthetic && (
            <span
              title="Theme tags are derived from creative content — PDO-grounded enrichment activates when the ontology engine is enabled for this account"
              className="text-[9px] text-slate-300 italic cursor-default"
            >
              theme-derived
            </span>
          )}
        </div>
      )}

      {/* ── Error banner ─────────────────────────────────────────────── */}
      {error && !loading && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <span>{error}</span>
          <button
            type="button"
            onClick={handleRefresh}
            className="ml-auto font-medium text-amber-800 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Brief idea grid ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {loading
          ? [0, 1, 2, 3].map((i) => <BriefIdeaTileSkeleton key={i} />)
          : ideas.map((idea) => (
              <BriefIdeaTile
                key={idea.idea_id}
                idea={idea}
                isCreating={creatingIdeaId === idea.idea_id}
                disabled={creatingIdeaId !== null && creatingIdeaId !== idea.idea_id}
                activeFilter={activeFilter}
                onClick={() => handleTileClick(idea)}
                onFilterByFactor={handleFilterByFactor}
              />
            ))}
        {!loading && <NewCustomBriefTile onClick={onOpenModal} />}
      </div>

      {/* Resonance legend */}
      {!loading && ideas.length > 0 && (
        <p className="text-[10px] text-slate-400 leading-relaxed">
          <span className="inline-block w-2 h-2 rounded-full bg-teal-400 mr-1 align-middle" />Fit = buyer scenario alignment ·{' '}
          <span className="inline-block w-2 h-2 rounded-full bg-indigo-400 mr-1 ml-1 align-middle" />Demand = keyword intent density ·{' '}
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1 ml-1 align-middle" />New = differentiation.
          {allSynthetic
            ? ' Tags are theme-derived — PDO enrichment activates when the ontology engine is enabled for this account.'
            : ' Scores are PDO-signal-weighted heuristics, not empirical predictions.'}
        </p>
      )}
    </section>
  );
}
